import { useEffect, useState } from 'react';
import { ApiError, companiesApi, type UpdateCompanyFeaturesPayload } from '../../lib/api';
import { useCompanyStore } from '../../stores/company';
import { useUiStore } from '../../stores/ui';
import { Toggle } from '../../components/Toggle';
import { Field, Select } from '../../components/Field';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Can } from '../../lib/permissions';
import { FEATURE_META, INVENTORY_DEPENDENT_FEATURES } from '../../lib/features';
import type { FeatureKey } from '../../lib/types';
import type { BusinessActivity } from '../../lib/types';
import { BUSINESS_ACTIVITIES, BUSINESS_ACTIVITY_META, activitySummary } from '../../lib/businessActivities';

const GROUPS: { title: string; keys: FeatureKey[] }[] = [
  { title: 'Inventory', keys: ['maintain_inventory', 'multi_godown', 'batches_and_expiry', 'serial_tracking', 'actual_vs_billed_qty'] },
  { title: 'Sales & purchase', keys: ['bill_wise_details', 'discount_columns', 'additional_charges', 'multiple_price_levels', 'order_processing'] },
  { title: 'Manufacturing & job work', keys: ['bom_manufacturing', 'job_work'] },
  { title: 'Other', keys: ['cost_centres', 'budgets', 'interest_calculation', 'gst_enabled'] },
];

export function CompanyFeaturesPage() {
  const activeCompanyId = useCompanyStore((s) => s.activeCompanyId);
  const features = useCompanyStore((s) => s.features);
  const setFeatures = useCompanyStore((s) => s.setFeatures);
  const pushToast = useUiStore((s) => s.pushToast);
  const [error, setError] = useState<unknown>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [blockedInfo, setBlockedInfo] = useState<{ key: string; message: string } | null>(null);

  useEffect(() => {
    if (!activeCompanyId) return;
    companiesApi.getFeatures(activeCompanyId).then(setFeatures).catch(setError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompanyId]);

  async function save(patch: Omit<UpdateCompanyFeaturesPayload, 'revision'>, key: string) {
    if (!activeCompanyId || !features) return;
    setSavingKey(key);
    setError(null);
    setBlockedInfo(null);
    try {
      const updated = await companiesApi.putFeatures(activeCompanyId, { ...patch, revision: features.revision });
      setFeatures(updated);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'FEATURE_TOGGLE_BLOCKED') {
        setBlockedInfo({ key, message: err.message });
      } else {
        setError(err);
      }
    } finally {
      setSavingKey(null);
    }
  }

  function toggleActivity(activity: BusinessActivity) {
    if (!features) return;
    const current: BusinessActivity[] = features.businessActivities?.length ? features.businessActivities : ['trading'];
    const next = current.includes(activity) ? current.filter((item) => item !== activity) : [...current, activity];
    if (!next.length) return;
    save({ businessActivities: next }, 'businessActivities');
  }

  if (!features) {
    return <div className="p-4 text-[12.5px] text-[var(--text-dim)]">Loading features…</div>;
  }

  return (
    <div className="mx-auto max-w-3xl p-4">
      <div className="mb-3 border-b border-[var(--border)] pb-2">
        <h1 className="text-[14px] font-semibold">Company features</h1>
        <p className="text-[11.5px] text-[var(--text-faint)]">
          Tally's F11 equivalent. A toggle changes UI and validation only — turning one off while dependent data exists is refused, never silently ignored.
        </p>
      </div>

      {error ? <ErrorBanner error={error} onDismiss={() => setError(null)} /> : null}

      <div className="flex flex-col gap-4">
        <section>
          <div className="mb-1 flex items-end justify-between gap-3">
            <div><h2 className="text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">How this company operates</h2><p className="mt-1 text-[11.5px] text-[var(--text-faint)]">Enable activities to add sensible tools. Removing one does not delete existing data.</p></div>
            <span className="mono text-[10px] text-[var(--accent)]">{activitySummary(features.businessActivities?.length ? features.businessActivities : ['trading'])}</span>
          </div>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {BUSINESS_ACTIVITIES.map((activity) => {
              const meta = BUSINESS_ACTIVITY_META[activity];
              const checked = (features.businessActivities?.length ? features.businessActivities : ['trading']).includes(activity);
              const lastSelected = checked && (features.businessActivities?.length ?? 1) === 1;
              return <Can key={activity} permission="company:features:edit" fallback={<div className="border border-[var(--border)] px-2.5 py-2"><span className="text-[12px]">{meta.mark} {meta.label}</span><span className="ml-2 text-[11px] text-[var(--text-faint)]">{checked ? 'On' : 'Off'}</span></div>}>
                <label className={`cursor-pointer rounded-[3px] border px-2.5 py-2 ${checked ? 'border-[var(--accent)] bg-[var(--bg-selected)]/40' : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'}`}><span className="flex items-start gap-2"><input type="checkbox" checked={checked} disabled={savingKey === 'businessActivities' || lastSelected} title={lastSelected ? 'At least one business activity is required.' : undefined} aria-label={`${meta.label}${lastSelected ? ' (at least one activity is required)' : ''}`} onChange={() => toggleActivity(activity)} className="mt-0.5" /><span><span className="text-[12px] font-medium">{meta.mark} {meta.label}</span><span className="block text-[11px] text-[var(--text-faint)]">{meta.blurb}</span></span></span></label>
              </Can>;
            })}
          </div>
        </section>
        {GROUPS.map((group) => (
          <div key={group.title}>
            <h2 className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">{group.title}</h2>
            <div className="flex flex-col divide-y divide-[var(--border)] border border-[var(--border)]">
              {group.keys.map((key) => {
                const meta = FEATURE_META[key];
                const dependsOnInventory = INVENTORY_DEPENDENT_FEATURES.includes(key);
                const disabled = key !== 'maintain_inventory' && dependsOnInventory && !features.flags.maintain_inventory;
                return (
                  <div key={key} className="flex flex-col gap-0.5 px-2.5 py-1.5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col">
                        <span className="text-[12.5px]">{meta.label}</span>
                        <span className="text-[11px] text-[var(--text-faint)]">{meta.help}</span>
                      </div>
                      <Can permission="company:features:edit" fallback={<span className="text-[11px] text-[var(--text-faint)]">{features.flags[key] ? 'On' : 'Off'}</span>}>
                        <Toggle
                          id={`feat-${key}`}
                          checked={features.flags[key]}
                          disabled={disabled || savingKey === key}
                          disabledReason={disabled ? 'Requires "Maintain inventory" to be turned on.' : undefined}
                          onChange={(checked) => save({ flags: { [key]: checked } as Partial<typeof features.flags> }, key)}
                        />
                      </Can>
                    </div>
                    {blockedInfo?.key === key && (
                      <p className="rounded-[2px] bg-[var(--danger-bg)] px-1.5 py-1 text-[11px] text-[var(--danger)]">{blockedInfo.message}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div>
          <h2 className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">Valuation & controls</h2>
          <div className="flex flex-col gap-2 border border-[var(--border)] p-2.5">
            <Field label="Tax rounding level" htmlFor="feat-rounding" inline>
              <Select
                id="feat-rounding"
                value={features.taxRoundingLevel}
                onChange={(e) => save({ taxRoundingLevel: e.target.value as typeof features.taxRoundingLevel }, 'taxRoundingLevel')}
              >
                <option value="invoice">Round at invoice level</option>
                <option value="line_item">Round at line-item level</option>
                <option value="none">No rounding</option>
              </Select>
            </Field>
            <Field label="Costing method" htmlFor="feat-costing" inline>
              <Select
                id="feat-costing"
                value={features.costingMethod}
                disabled={!features.flags.maintain_inventory}
                onChange={(e) => save({ costingMethod: e.target.value as typeof features.costingMethod }, 'costingMethod')}
              >
                <option value="fifo">FIFO</option>
                <option value="weighted_average">Weighted average</option>
              </Select>
            </Field>
            <div className="flex items-center justify-between">
              <span className="w-40 shrink-0 text-[12.5px]">Allow negative stock</span>
              <Toggle
                checked={features.allowNegativeStock}
                disabled={!features.flags.maintain_inventory}
                disabledReason={!features.flags.maintain_inventory ? 'Requires "Maintain inventory".' : undefined}
                onChange={(checked) => save({ allowNegativeStock: checked }, 'allowNegativeStock')}
              />
            </div>
          </div>
        </div>
      </div>

      <p className="mt-3 font-mono text-[10.5px] text-[var(--text-faint)]">revision {features.revision}</p>
    </div>
  );
}
